/*
How it behaves
WASD pans the camera in the ground plane, relative to the camera's authored facing direction. W goes "into" the scene, S pulls back, A and D strafe.
Up / Down arrows raise and lower the focus target vertically. Because the diorama camera computes the eye from target + yaw/pitch/distance, moving the target up moves the entire camera up by the same amount.
= / - (or numpad) zoom in and out by adjusting distance. + shrinks distance (zoom in), - grows it (zoom out).
Shift / Ctrl scale all steps by fast_multiplier or slow_multiplier.

Shift + O toggles between continuous and step mode without conflicting with the other placement chords.

Why this matches your camera model
The DioramaCamera formula derives eye from target + yaw/pitch/distance, so:
moving the target moves the eye by the same vector → pure pan,
changing distance changes the eye along the camera ray → pure zoom,
yaw and pitch stay locked, preserving the authored angle.
That is exactly the "pan and zoom only" constraint you described, and the code never touches yaw or pitch so the authored shot language is preserved.

One subtle detail
The forward_xz vector points from the eye to the target on the ground plane. That means W moves the camera "into" the scene relative to the camera angle, which feels natural even when the camera is yawed off-axis. The strafe vector is computed as a 90° rotation of forward on the ground plane, so A and D are always perpendicular to W and S regardless of yaw.
*/
import InputService, { Keys, KeyChord } from "../input/inputservice";
import Stage from "../stage/stage";
import DioramaCamera from "./diorama_camera";

export type DioramaCameraPlacementMode = "continuous" | "step";

export default class DioramaCameraPlacement {
  input: InputService;
  camera: DioramaCamera;

  pan_increment: number = 0.5;
  pan_speed: number = 1;
  zoom_increment: number = 0.5;
  zoom_speed: number = 1;
  vertical_increment: number = 0.3;
  vertical_speed: number = 1;

  mode: DioramaCameraPlacementMode = "continuous";

  fast_modifier_key: string = Keys.Shift;
  slow_modifier_key: string = Keys.Ctrl;
  fast_multiplier: number = 4.0;
  slow_multiplier: number = 0.25;

  step_size_pan: number = 0.1;
  step_size_zoom: number = 0.2;
  step_size_vertical: number = 0.1;

  toggle_mode_chord: KeyChord = { required: [Keys.Shift, Keys.O] };

  constructor(camera: DioramaCamera) {
    this.camera = camera;
    this.input = Stage.instance().input_service;
  }

  set_speed(
    pan_increment: number,
    pan_speed: number,
    zoom_increment: number,
    zoom_speed: number,
    vertical_increment: number,
    vertical_speed: number
  ): void {
    this.pan_increment = pan_increment;
    this.pan_speed = pan_speed;
    this.zoom_increment = zoom_increment;
    this.zoom_speed = zoom_speed;
    this.vertical_increment = vertical_increment;
    this.vertical_speed = vertical_speed;
  }

  set_mode(mode: DioramaCameraPlacementMode): void {
    this.mode = mode;
  }

  set_step_sizes(pan: number, zoom: number, vertical: number): void {
    this.step_size_pan = pan;
    this.step_size_zoom = zoom;
    this.step_size_vertical = vertical;
  }
    tick(delta_time: number): void {
    if (this.input.was_chord_pressed(this.toggle_mode_chord)) {
      this.mode = this.mode === "continuous" ? "step" : "continuous";
    }

    const modifier = this.get_modifier_multiplier();

    let strafe_dir = 0;
    let forward_dir = 0;

    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.D)) strafe_dir  += 1;
      if (this.input.is_key_down(Keys.A)) strafe_dir  -= 1;
      if (this.input.is_key_down(Keys.W)) forward_dir += 1;
      if (this.input.is_key_down(Keys.S)) forward_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.D)) strafe_dir  += 1;
      if (this.input.was_key_pressed(Keys.A)) strafe_dir  -= 1;
      if (this.input.was_key_pressed(Keys.W)) forward_dir += 1;
      if (this.input.was_key_pressed(Keys.S)) forward_dir -= 1;
    }

    let vertical_dir = 0;
    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.Up))   vertical_dir += 1;
      if (this.input.is_key_down(Keys.Down)) vertical_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.Up))   vertical_dir += 1;
      if (this.input.was_key_pressed(Keys.Down)) vertical_dir -= 1;
    }

    let zoom_dir = 0;
    if (this.mode === "continuous") {
      if (
        this.input.is_key_down(Keys.Equal) ||
        this.input.is_key_down(Keys.NumpadPlus)
      ) zoom_dir -= 1;

      if (
        this.input.is_key_down(Keys.Minus) ||
        this.input.is_key_down(Keys.NumpadMinus)
      ) zoom_dir += 1;
    } else {
      if (
        this.input.was_key_pressed(Keys.Equal) ||
        this.input.was_key_pressed(Keys.NumpadPlus)
      ) zoom_dir -= 1;

      if (
        this.input.was_key_pressed(Keys.Minus) ||
        this.input.was_key_pressed(Keys.NumpadMinus)
      ) zoom_dir += 1;
    }

    let changed_target = false;

    if (strafe_dir !== 0 || forward_dir !== 0) {
      const step =
        this.mode === "continuous"
          ? this.pan_speed * this.pan_increment * delta_time * modifier
          : this.step_size_pan * modifier;

      const forward_xz = this.camera.get_forward_xz();
      const fx = forward_xz[0];
      const fz = forward_xz[1];

      const rx = fz;
      const rz = -fx;

      const dx = (rx * strafe_dir + fx * forward_dir) * step;
      const dz = (rz * strafe_dir + fz * forward_dir) * step;

      this.camera.target[0] += dx;
      this.camera.target[2] += dz;
      changed_target = true;
    }

    if (vertical_dir !== 0) {
      const step =
        this.mode === "continuous"
          ? this.vertical_speed * this.vertical_increment * delta_time * modifier
          : this.step_size_vertical * modifier;

      this.camera.target[1] += vertical_dir * step;
      changed_target = true;
    }

    if (zoom_dir !== 0) {
      const step =
        this.mode === "continuous"
          ? this.zoom_speed * this.zoom_increment * delta_time * modifier
          : this.step_size_zoom * modifier;

      const new_distance = this.camera.distance + zoom_dir * step;
      this.camera.set_distance(new_distance);
      this.log_camera_change("zoom");
    }

    if (changed_target) {
      this.camera.set_target(
        this.camera.target[0],
        this.camera.target[1],
        this.camera.target[2]
      );
      this.log_camera_change("pan");
    }
  }
    private get_modifier_multiplier(): number {
    let mult = 1.0;

    if (this.input.is_key_down(this.fast_modifier_key)) {
      mult *= this.fast_multiplier;
    }

    if (this.input.is_key_down(this.slow_modifier_key)) {
      mult *= this.slow_multiplier;
    }

    return mult;
  }

  private log_camera_change(kind: "pan" | "zoom"): void {
    const f = (n: number) => n.toFixed(3);

    if (kind === "pan") {
      console.log(
        `camera target ${f(this.camera.target[0])},${f(this.camera.target[1])},${f(this.camera.target[2])}`
      );
    } else {
      console.log(`camera distance ${f(this.camera.distance)}`);
    }
  }
}